import { useEffect, useState, useMemo } from 'react';
import { HYDRATION_STATUS, FEEDING_THRESHOLDS } from '../config/constants';

/**
 * Estado del semáforo de hidratación (verde/amarillo/rojo) en función de la
 * última vez que el balde recibió agua y el intervalo configurado del grupo.
 *
 * Reutiliza FEEDING_THRESHOLDS porque el modelo de status (50% verde / 90%
 * amarillo / >=90% rojo) es invariante al dominio — la única diferencia es
 * el campo del que se lee `lastEventMs`. Si más adelante el feedback de
 * usuarios pide thresholds distintos para hidratación, extraemos
 * HYDRATION_THRESHOLDS.
 *
 * NOTA: lógica intencionalmente espejada de `useFeedingStatus` para mantener
 * naming local (lastWatered vs lastFed) sin abstraer prematuramente. Si tocás
 * uno, mirá el otro.
 */

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return null;
};

export const computeHydrationStatus = ({ lastWateredMs, intervalHours, nowMs }) => {
  if (!intervalHours || intervalHours <= 0) {
    return {
      status: HYDRATION_STATUS.UNKNOWN,
      elapsedMs: null,
      intervalMs: null,
      fractionElapsed: null,
      hoursUntilNext: null,
    };
  }

  const intervalMs = intervalHours * 3_600_000;

  if (!lastWateredMs) {
    return {
      status: HYDRATION_STATUS.UNKNOWN,
      elapsedMs: null,
      intervalMs,
      fractionElapsed: null,
      hoursUntilNext: null,
    };
  }

  const elapsedMs = Math.max(0, nowMs - lastWateredMs);
  const fractionElapsed = elapsedMs / intervalMs;

  let status;
  if (fractionElapsed < FEEDING_THRESHOLDS.GREEN_FRACTION) {
    status = HYDRATION_STATUS.GREEN;
  } else if (fractionElapsed < FEEDING_THRESHOLDS.YELLOW_FRACTION) {
    status = HYDRATION_STATUS.YELLOW;
  } else {
    status = HYDRATION_STATUS.RED;
  }

  const hoursUntilNext = Math.max(0, (intervalMs - elapsedMs) / 3_600_000);

  return {
    status,
    elapsedMs,
    intervalMs,
    fractionElapsed,
    hoursUntilNext,
  };
};

/**
 * Hook reactivo que recalcula el estado del semáforo cada minuto.
 *
 * @param {object} group - hydrationGroup con lastWateredAt y hydrationIntervalHours
 * @param {object} [options]
 * @param {number} [options.refreshIntervalMs=60_000]
 *
 * @returns {object} { status, elapsedMs, intervalMs, fractionElapsed, hoursUntilNext, isRecentlyWatered }
 */
export const useHydrationStatus = (group, { refreshIntervalMs = 60_000 } = {}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!group?.lastWateredAt) return;
    const id = setInterval(() => setNow(Date.now()), refreshIntervalMs);
    return () => clearInterval(id);
  }, [group?.lastWateredAt, refreshIntervalMs]);

  return useMemo(() => {
    const lastWateredMs = toMillis(group?.lastWateredAt);
    const intervalHours = group?.hydrationIntervalHours ?? null;

    const result = computeHydrationStatus({
      lastWateredMs,
      intervalHours,
      nowMs: now,
    });

    // isRecentlyWatered: <25% del intervalo. Mismo umbral que isRecentlyFed.
    // Dispara el modal de confirmación "¿Seguro? Hace X horas que tomaron".
    const isRecentlyWatered =
      result.fractionElapsed !== null && result.fractionElapsed < 0.25;

    return { ...result, isRecentlyWatered };
  }, [group?.lastWateredAt, group?.hydrationIntervalHours, now]);
};
